import React from 'react';
import type { AgendaItem } from '../types';

type TFn = (key: string, vars?: Record<string, string | number | boolean | null | undefined>, fallback?: string) => string;

interface Props {
    items: AgendaItem[];
    groupByMonth?: boolean;
    locale?: string;
    t: TFn;
}

/** Mois "YYYY-MM" → libellé localisé (ex. "septembre 2026"). */
function monthKey(item: AgendaItem): string {
    const d = item.date_start ? new Date(item.date_start.replace(' ', 'T')) : null;
    if (!d || Number.isNaN(d.getTime())) {
        return '';
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string, locale: string): string {
    const [y, m] = key.split('-').map(Number);
    if (!y || !m) {
        return '';
    }
    const d = new Date(y, m - 1, 1);
    const label = d.toLocaleDateString(locale || 'fr', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function ctaFor(item: AgendaItem, t: TFn): React.ReactElement | null {
    const cta = item.cta;
    if (cta && cta.mode === 'none') {
        return null;
    }
    if (cta && cta.mode === 'external_link' && cta.url) {
        return (
            <a
                className="gas-button gas-button--primary gas-button--small gas-agenda-compact__cta"
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
            >
                {cta.label || t('activite.event_cta.default_label', {}, 'Réserver')}
            </a>
        );
    }
    return (
        <a className="gas-button gas-button--outline gas-button--small gas-agenda-compact__cta" href={item.url}>
            {t('agenda.compact.view', {}, 'Voir')}
        </a>
    );
}

function Row({ item, t }: { item: AgendaItem; t: TFn }): React.ReactElement {
    const parent = item.parent;
    const isSessionOfParent = !!(parent && item.list_hidden);
    const needsParent = !!item.prerequis_parent;

    return (
        <li
            className={`gas-agenda-compact__row${parent ? ' gas-agenda-compact__row--linked' : ''}`}
            data-type={item.type}
            data-title={item.title.toLowerCase()}
        >
            <span className="gas-agenda-compact__date">{item.date_short || item.date_label}</span>
            <span className="gas-agenda-compact__body">
                <a className="gas-agenda-compact__title" href={item.url}>{item.title}</a>
                {item.lieu ? (
                    <span className="gas-agenda-compact__venue">
                        <i className="fas fa-map-marker-alt" aria-hidden="true" /> {item.lieu}
                    </span>
                ) : null}
                {parent ? (
                    <span className="gas-agenda-compact__meta-line">
                        <span className="gas-agenda-compact__linked">
                            {isSessionOfParent
                                ? t('agenda.compact.linked_to_prefix', {}, 'Lié à')
                                : t('agenda.compact.part_of_prefix', {}, 'Fait partie de')}
                            {' '}
                            {parent.url ? (
                                <a
                                    className="gas-agenda-compact__parent-link"
                                    href={parent.url}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {parent.title}
                                </a>
                            ) : (
                                <span>{parent.title}</span>
                            )}
                        </span>
                        {needsParent ? (
                            <span className="gas-agenda-compact__prereq">
                                {' · '}
                                {t('agenda.compact.prereq_parent', {}, 'participation au parent requise')}
                            </span>
                        ) : null}
                    </span>
                ) : null}
            </span>
            <span className="gas-agenda-compact__type">{item.type_label}</span>
            {ctaFor(item, t)}
        </li>
    );
}

/** Vue compacte (liste) — optionnellement groupée par mois. */
export function AgendaCompactView({ items, groupByMonth = false, locale = 'fr', t }: Props): React.ReactElement {
    // Tri chronologique explicite (évite un ordre bizarre si le read-model
    // mélange parents + sessions list_hidden filtrées en amont).
    const sorted = [...items].sort((a, b) => {
        const da = a.date_start || '';
        const db = b.date_start || '';
        if (da !== db) {
            return da < db ? -1 : 1;
        }
        return a.id - b.id;
    });

    if (!groupByMonth) {
        return (
            <ul className="gas-agenda-compact">
                {sorted.map((it) => <Row key={it.id} item={it} t={t} />)}
            </ul>
        );
    }

    const groups: Record<string, AgendaItem[]> = {};
    sorted.forEach((it) => {
        const k = monthKey(it) || '—';
        if (!groups[k]) {
            groups[k] = [];
        }
        groups[k].push(it);
    });
    // Mois en ordre chronologique croissant (clés YYYY-MM triables).
    const order = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    return (
        <div className="gas-agenda-compact-months">
            {order.map((k) => (
                <section key={k} className="gas-agenda-compact-months__group">
                    <h4 className="gas-agenda-compact-months__label">{monthLabel(k, locale)}</h4>
                    <ul className="gas-agenda-compact">
                        {groups[k].map((it) => <Row key={it.id} item={it} t={t} />)}
                    </ul>
                </section>
            ))}
        </div>
    );
}
